import { Avatar, Icon, useToast } from "@uzverse/ui";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { OrgCompany } from "../api/types.js";
import { useAssistant } from "../state/assistant.js";
import { useSession } from "../state/session.js";
import { voicesForLanguage } from "../pages/SettingsPage.js";
import { playPreview } from "../voice/preview.js";
import { useErrorText } from "./common.js";
import { useWorldT } from "../world/i18n.js";
import { useWorldState, worldStore } from "../world/store.js";
import { describeTarget } from "../world/target.js";
import { AssistantModelPicker } from "./ModelPicker.js";
import { AssistantPortrait } from "./AssistantPortrait.js";

const usd = (n: number) => `$${n.toFixed(2)}`;

/**
 * What the user selected in the 3D world, in words ("Demo Café · Floor 2 · north wall"): this is
 * the target that goes with the next message, so "change this wall" means that wall.
 */
function LookingAt() {
  const t = useWorldT();
  const state = useWorldState();
  const node = state.selected ? worldStore.node(state.selected.id) : undefined;
  return (
    <section className="ctx-section" data-testid="context-looking-at">
      <h3 className="eyebrow">{t("target.title")}</h3>
      {state.selected && node ? (
        <div className="stack">
          <p>{describeTarget(t, node, state.selected.part)}</p>
          <button type="button" className="btn btn-sm" onClick={() => worldStore.select(null)}>
            {t("target.clear")}
          </button>
        </div>
      ) : (
        <p className="muted small">{t("target.none")}</p>
      )}
    </section>
  );
}

/** Right-hand panel of the chat: where the work happens, assistant settings, budget. */
export function ContextPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation("web");
  const toast = useToast();
  const errorText = useErrorText();
  const { services } = useSession();
  const { settings, catalog, save, name } = useAssistant();
  const [org, setOrg] = useState<OrgCompany[] | null>(null);
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    services.api
      .listOrganization()
      .then((o) => !cancelled && setOrg(o))
      .catch(() => !cancelled && setOrg([]));
    return () => {
      cancelled = true;
    };
  }, [services]);

  // The panel shows the first company; picking a "current" one arrives with the org API.
  const company = org?.[0];
  const building = company?.buildings[0];
  const floor = building?.floors[0];
  const voices = catalog ? voicesForLanguage(catalog, i18n.language) : [];
  const voiceKey = settings?.voice ? `${settings.voice.providerId}::${settings.voice.voiceId}` : "";
  const canPreview =
    settings?.voice &&
    catalog?.providers.find((p) => p.id === settings.voice?.providerId)?.capabilities["preview"] ===
      true;

  async function patch(p: Parameters<typeof save>[0]) {
    try {
      await save(p);
      toast.show(t("context.saved"));
    } catch (err) {
      toast.show(errorText(err), "error");
    }
  }

  return (
    <aside className="context" aria-label={t("context.title")}>
      <header className="context-head">
        <h2>{t("context.title")}</h2>
        <button
          type="button"
          className="btn btn-quiet btn-icon"
          aria-label={t("nav.hideContext")}
          onClick={onClose}
        >
          <Icon name="x" />
        </button>
      </header>

      <section className="ctx-section">
        <h3 className="eyebrow">{t("context.company")}</h3>
        {company ? (
          <dl className="ctx-list">
            <div>
              <dt>
                <Icon name="building" size={14} /> {t("context.company")}
              </dt>
              <dd>{company.name}</dd>
            </div>
            {building && (
              <div>
                <dt>
                  <Icon name="cube" size={14} /> {t("context.building")}
                </dt>
                <dd>{building.name}</dd>
              </div>
            )}
            {floor && (
              <div>
                <dt>
                  <Icon name="layers" size={14} /> {t("context.floor")}
                </dt>
                <dd>{floor.name}</dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="muted small">{org === null ? "…" : t("context.none")}</p>
        )}
      </section>

      <LookingAt />

      <section className="ctx-section">
        <h3 className="eyebrow">{t("context.assistant")}</h3>
        {settings ? (
          <div className="stack">
            <div className="row">
              <Avatar name={name} assistant size={28} />
              <label className="grow">
                <span className="sr-only">{t("shell.assistantName")}</span>
                <input
                  value={draft ?? settings.name}
                  maxLength={40}
                  onChange={(e) => setDraft(e.target.value)}
                  onBlur={() => {
                    const next = (draft ?? "").trim();
                    if (draft !== null && next && next !== settings.name)
                      void patch({ name: next });
                    setDraft(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  }}
                />
              </label>
            </div>
            <div className="field">
              <span>{t("context.appearance")}</span>
              <AssistantPortrait />
              <Link className="link small" to="/settings">
                {t("avatar.assistantLook")}
              </Link>
            </div>
            <label className="field">
              <span>{t("context.voice")}</span>
              <select
                value={voiceKey}
                onChange={(e) => {
                  const [providerId, voiceId] = e.target.value.split("::");
                  void patch({ voice: providerId && voiceId ? { providerId, voiceId } : null });
                }}
              >
                <option value="">{t("context.noVoice")}</option>
                {voices.map((v) => (
                  <option key={`${v.providerId}::${v.id}`} value={`${v.providerId}::${v.id}`}>
                    {v.name}
                  </option>
                ))}
              </select>
            </label>
            <AssistantModelPicker compact />
            {canPreview && settings.voice && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  const v = settings.voice;
                  if (!v) return;
                  services.api
                    .previewVoice(v.providerId, v.voiceId)
                    .then(playPreview)
                    .catch((err: unknown) => toast.show(errorText(err), "error"));
                }}
              >
                <Icon name="volume" size={14} />
                {t("settings.voice.preview")}
              </button>
            )}
          </div>
        ) : (
          <span className="skeleton" />
        )}
      </section>

      <section className="ctx-section">
        <h3 className="eyebrow">{t("context.budget")}</h3>
        {company?.budget ? (
          <div className="stack">
            <p className="tnum">
              {company.budget.limitUsd === null
                ? t("context.budgetOpen", { used: usd(company.budget.usedUsd) })
                : t("context.budgetOf", {
                    used: usd(company.budget.usedUsd),
                    limit: usd(company.budget.limitUsd),
                  })}
            </p>
            {company.budget.limitUsd !== null && (
              <div
                className="meter"
                role="meter"
                aria-label={t("context.budget")}
                aria-valuemin={0}
                aria-valuemax={company.budget.limitUsd}
                aria-valuenow={company.budget.usedUsd}
              >
                <span
                  style={{
                    width: `${Math.min(100, (company.budget.usedUsd / (company.budget.limitUsd || 1)) * 100)}%`,
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="muted small">{t("context.budgetNone")}</p>
        )}
      </section>

      <Link className="link small" to="/settings">
        {t("context.allSettings")}
      </Link>
    </aside>
  );
}
